import { useEffect } from 'react';
import { get, pick } from 'lodash';
import Modal from 'react-modal';
import ImageViewer from '../../../ImageViewer';
import { useCompareManagement } from '../../../SideMenuSearchLane/context/compare';
import { IDCARD_SECTIONS_ENUM } from '../../../SideMenuSearchLane/context/compare/constants';

import WeightBreakdown from '../../../WeightBreakdown';
import MainTieIns from '../../../MainTieIns';
import EquipmentList from '../../../EquipmentList';
import DocumentationList from '../../../DocumentationList';
import OtherRelevantDocuments from '../../../OtherRelevantDocuments';

import {
  Wrapper,
  CloseButton,
  ModalTitle,
  ContentWrapperHorizontal,
  Lane,
  ContentWrapperVertical,
  LaneTitle,
  Scrollable,
  IdCardNameWrapper,
} from './styles';

const VerticalLaneComponent = ({ moduleNo, name, children }) => (
  <Lane>
    <LaneTitle>
      <label className='highlighted-label'>{moduleNo}</label> {name}
    </LaneTitle>
    <Scrollable>{children}</Scrollable>
  </Lane>
);

const ComparisonModal = ({ onModalClose, isMenuOpen }) => {
  const { sectionInComparisonMode } = useCompareManagement();
  const { sectionId, metadata } = sectionInComparisonMode;

  const metadataPayload = {
    left: {
      name: metadata?.left?.idCard?.name || 'untitled',
      moduleNo: metadata?.left?.idCard?.tsCode,
    },
    right: {
      name: metadata?.right?.idCard?.name || 'untitled',
      moduleNo: metadata?.right?.idCard?.tsCode,
    },
  };

  const SectionConfigMap = {
    [IDCARD_SECTIONS_ENUM.ViewsAndLayouts]: {
      isHorizontal: true,
      render: () => {
        const { left, right } = metadata;

        return (
          <>
            <Lane>
              <LaneTitle>
                <label>{left?.image?.legend || 'untitled'}</label>
              </LaneTitle>
              <ImageViewer
                fullscreen={false}
                imageId={left?.image?.imageId}
                containerStyle={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </Lane>
            <Lane>
              <LaneTitle>
                <label>{right?.image?.legend || 'untitled'}</label>
              </LaneTitle>
              <ImageViewer
                fullscreen={false}
                imageId={right?.image?.imageId}
                containerStyle={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </Lane>
          </>
        );
      },
    },
    [IDCARD_SECTIONS_ENUM.WeightBreakdown]: {
      isHorizontal: false,
      render: () => (
        <>
          <VerticalLaneComponent
            moduleNo={metadataPayload.left.moduleNo}
            name={metadataPayload.left.name}>
            <WeightBreakdown idCardData={metadata?.left?.idCard} />
          </VerticalLaneComponent>
          <VerticalLaneComponent
            moduleNo={metadataPayload.right.moduleNo}
            name={metadataPayload.right.name}>
            <WeightBreakdown idCardData={metadata?.right?.idCard} />
          </VerticalLaneComponent>
        </>
      ),
    },
    [IDCARD_SECTIONS_ENUM.TieIns]: {
      isHorizontal: false,
      render: () => (
        <>
          <VerticalLaneComponent
            moduleNo={metadataPayload.left.moduleNo}
            name={metadataPayload.left.name}>
            <MainTieIns
              {...pick(metadata?.left?.idCard, [
                'tieIns',
                'tieInsComment',
                'tieInsOtherUtilities',
                'tieInsOtherNetworks',
              ])}
            />
          </VerticalLaneComponent>
          <VerticalLaneComponent
            moduleNo={metadataPayload.right.moduleNo}
            name={metadataPayload.right.name}>
            <MainTieIns
              {...pick(metadata?.right?.idCard, [
                'tieIns',
                'tieInsComment',
                'tieInsOtherUtilities',
                'tieInsOtherNetworks',
              ])}
            />
          </VerticalLaneComponent>
        </>
      ),
    },
    [IDCARD_SECTIONS_ENUM.EquipmentList]: {
      isHorizontal: false,
      render: () => (
        <>
          <VerticalLaneComponent
            moduleNo={metadataPayload.left.moduleNo}
            name={metadataPayload.left.name}>
            <EquipmentList {...pick(metadata?.left?.idCard, ['equipmentList', 'equipmentListComment'])} />
          </VerticalLaneComponent>
          <VerticalLaneComponent
            moduleNo={metadataPayload.right.moduleNo}
            name={metadataPayload.right.name}>
            <EquipmentList {...pick(metadata?.right?.idCard, ['equipmentList', 'equipmentListComment'])} />
          </VerticalLaneComponent>
        </>
      ),
    },
    [IDCARD_SECTIONS_ENUM.DocumentList]: {
      isHorizontal: false,
      render: () => (
        <>
          <VerticalLaneComponent
            moduleNo={metadataPayload.left.moduleNo}
            name={metadataPayload.left.name}>
            <DocumentationList documents={metadata?.left?.idCard?.documents} />
          </VerticalLaneComponent>
          <VerticalLaneComponent
            moduleNo={metadataPayload.right.moduleNo}
            name={metadataPayload.right.name}>
            <DocumentationList documents={metadata?.right?.idCard?.documents} />
          </VerticalLaneComponent>
        </>
      ),
    },
    [IDCARD_SECTIONS_ENUM.OtherDocumentList]: {
      isHorizontal: false,
      render: () => (
        <>
          <VerticalLaneComponent
            moduleNo={metadataPayload.left.moduleNo}
            name={metadataPayload.left.name}>
            <OtherRelevantDocuments otherDocuments={metadata?.left?.idCard?.otherDocuments} />
          </VerticalLaneComponent>
          <VerticalLaneComponent
            moduleNo={metadataPayload.right.moduleNo}
            name={metadataPayload.right.name}>
            <OtherRelevantDocuments otherDocuments={metadata?.right?.idCard?.otherDocuments} />
          </VerticalLaneComponent>
        </>
      ),
    },
    [IDCARD_SECTIONS_ENUM.LocationOnFPSO]: {
      isHorizontal: true,
      render: () => {
        const { left, right } = metadata;

        return (
          <>
            <Lane>
              <LaneTitle>
                <label>{left?.image?.legend || 'untitled'}</label>
              </LaneTitle>
              <ImageViewer
                fullscreen={false}
                imageId={left?.image?.imageId}
                containerStyle={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </Lane>
            <Lane>
              <LaneTitle>
                <label>{right?.image?.legend || 'untitled'}</label>
              </LaneTitle>
              <ImageViewer
                fullscreen={false}
                imageId={right?.image?.imageId}
                containerStyle={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </Lane>
          </>
        );
      },
    },
  };

  const sectionConfig = get(SectionConfigMap, sectionId);
  const LayoutComponent = sectionConfig.isHorizontal ? ContentWrapperHorizontal : ContentWrapperVertical;

  const shouldRenderIdCardTitle = [IDCARD_SECTIONS_ENUM.ViewsAndLayouts, IDCARD_SECTIONS_ENUM.LocationOnFPSO].includes(
    sectionId
  );

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc
      onRequestClose={onModalClose}
      style={{
        content: {
          left: isMenuOpen ? 'calc(50% - 200px)' : '50%',
          transition: 'left 0.5s ease-out',
        },
      }}>
      <Wrapper>
        <ModalTitle>
          Compare {sectionId}
          {shouldRenderIdCardTitle && (
            <IdCardNameWrapper>
              <label className='text'>
                <span className='highlighted-label'>{metadataPayload.left.moduleNo}</span>
                {metadataPayload.left.name}
              </label>
              <label className='text'>
                <span className='highlighted-label'>{metadataPayload.right.moduleNo}</span>
                {metadataPayload.right.name}
              </label>
            </IdCardNameWrapper>
          )}
        </ModalTitle>
        <CloseButton onClick={() => onModalClose()} />
        <LayoutComponent>{sectionConfig.render()}</LayoutComponent>
      </Wrapper>
    </Modal>
  );
};

export default ComparisonModal;
